import { ImgHTMLAttributes, useState } from 'react';

import noImage from '../assets/NoImage.svg';
import { cn } from '../lib/utils';

type ProductImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src?: string | null;
  /** Классы только для заглушки — например, отступ, чтобы в большом блоке
   *  иконка не растягивалась на всю площадь. */
  placeholderClassName?: string;
  /** Своя заглушка вместо «No Image» — например, логотип у промокодов. */
  fallbackSrc?: string;
};

// Бэкенд иногда присылает не null, а строку «null» — это тоже «картинки нет».
const normalize = (src?: string | null) => {
  const value = src?.trim();
  return value && value !== 'null' && value !== 'undefined' ? value : null;
};

/**
 * Картинка товара или категории с заглушкой «No Image» (макет 2995-102007).
 *
 * Заглушка встаёт на место самого `<img>` с теми же классами, поэтому размеры
 * блока и вёрстка не меняются. Показываем её, если ссылки нет или она не
 * загрузилась. Запоминаем именно упавший адрес, а не флаг «ошибка»: пришёл
 * с бэкенда новый URL — сразу пробуем его, без перезапуска приложения.
 */
export const ProductImage = ({
  src,
  className,
  placeholderClassName,
  fallbackSrc = noImage,
  onError,
  alt = '',
  ...rest
}: ProductImageProps) => {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const url = normalize(src);
  const showPlaceholder = !url || failedSrc === url;

  return (
    <img
      {...rest}
      alt={alt}
      src={showPlaceholder ? fallbackSrc : url}
      onError={event => {
        if (!showPlaceholder) setFailedSrc(url);
        onError?.(event);
      }}
      className={cn(
        className,
        // Иконка квадратная: обрезать её по object-cover нельзя.
        showPlaceholder && cn('object-contain', placeholderClassName)
      )}
    />
  );
};
